import React, { useState, useEffect, useCallback } from 'react';
import { ChartBarIcon, ChevronDownIcon, ChevronUpIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import campaignService, { Campaign, CampaignStats } from '../../services/campaignService';

interface CampaignHistoryEntry extends Campaign {
  segments?: string[];
  stats?: CampaignStats | null;
}

const CampaignHistory: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [dateFilter, setDateFilter] = useState({
    start: '',
    end: ''
  });
  const [segmentFilter, setSegmentFilter] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const loadCampaignHistory = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load sent campaigns
      const response = await fetch(`${API_BASE_URL}/api/campaigns?status=sent`, {
        headers: authService.getAuthHeaders()
      });

      if (response.ok) {
        const sentCampaigns = await response.json();
        
        // Load segments and stats for each campaign
        const campaignsWithDetails = await Promise.all(
          sentCampaigns.map(async (campaign: Campaign) => {
            const [segmentsResponse, statsResponse] = await Promise.all([
              fetch(`${API_BASE_URL}/api/campaigns/${campaign.id}/segments`, {
                headers: authService.getAuthHeaders()
              }).catch(() => null),
              campaignService.getCampaignStats(campaign.id).catch(() => null)
            ]);

            let segments: string[] = [];
            if (segmentsResponse?.ok) {
              const segmentData = await segmentsResponse.json();
              segments = segmentData.map((s: any) => s.name);
            }

            const stats = statsResponse?.stats || null;

            return {
              ...campaign,
              segments,
              stats
            };
          })
        );

        setCampaigns(campaignsWithDetails);
      }
    } catch (error) {
      console.error('Error loading campaign history:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    loadCampaignHistory();
  }, [loadCampaignHistory]);

  const toggleExpanded = (campaignId: number) => {
    const newExpanded = new Set(expandedCampaigns);
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId);
    } else {
      newExpanded.add(campaignId);
    }
    setExpandedCampaigns(newExpanded);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  const exportToCSV = () => {
    const csvHeaders = [
      'Campaign Name',
      'Subject',
      'Sent Date',
      'Segments',
      'Recipients',
      'Opens',
      'Clicks',
      'Open Rate',
      'Click Rate'
    ];

    const csvData = campaigns.map(campaign => [
      campaign.name,
      campaign.subject,
      campaign.sent_at ? formatDate(campaign.sent_at) : '',
      campaign.segments?.join('; ') || '',
      campaign.estimated_recipients || 0,
      campaign.stats?.opens || 0,
      campaign.stats?.clicks || 0,
      campaign.stats ? formatRate(campaign.stats.open_rate) : '0%',
      campaign.stats ? formatRate(campaign.stats.click_rate) : '0%'
    ]);

    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `campaign-history-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    if (dateFilter.start && campaign.sent_at) {
      const sentDate = new Date(campaign.sent_at);
      const startDate = new Date(dateFilter.start);
      if (sentDate < startDate) return false;
    }

    if (dateFilter.end && campaign.sent_at) {
      const sentDate = new Date(campaign.sent_at);
      const endDate = new Date(dateFilter.end);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date
      if (sentDate > endDate) return false;
    }

    if (segmentFilter && !campaign.segments?.some(segment => 
      segment.toLowerCase().includes(segmentFilter.toLowerCase())
    )) {
      return false;
    }

    return true;
  });

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <ChartBarIcon className="h-5 w-5 mr-2 text-gray-500" />
            Campaign History
          </h3>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              id="start-date"
              type="date"
              value={dateFilter.start}
              onChange={(e) => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          
          <div>
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              id="end-date"
              type="date"
              value={dateFilter.end}
              onChange={(e) => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="segment-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Segment
            </label>
            <input
              id="segment-filter"
              type="text"
              placeholder="Search segments..."
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(e.target.value)}
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredCampaigns.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No sent campaigns found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredCampaigns.map(campaign => {
              const isExpanded = expandedCampaigns.has(campaign.id);
              
              return (
                <div key={campaign.id} className="border rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleExpanded(campaign.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {campaign.name}
                          </h4>
                          <div className="flex items-center ml-2">
                            {campaign.stats && (
                              <div className="flex items-center space-x-4 text-xs text-gray-500">
                                <span>Opens: {formatRate(campaign.stats.open_rate)}</span>
                                <span>Clicks: {formatRate(campaign.stats.click_rate)}</span>
                              </div>
                            )}
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-400 ml-2" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-400 ml-2" />
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-1 flex items-center text-sm text-gray-500">
                          <span>
                            Sent: {campaign.sent_at ? formatDate(campaign.sent_at) : 'Unknown'}
                          </span>
                          <span className="mx-2">•</span>
                          <span>{campaign.estimated_recipients} recipients</span>
                          {campaign.segments && campaign.segments.length > 0 && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{campaign.segments.join(', ')}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 border-t bg-gray-50">
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 mb-2">Campaign Details</h5>
                          <dl className="space-y-1 text-sm">
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Subject:</dt>
                              <dd className="text-gray-900">{campaign.subject}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Recipients:</dt>
                              <dd className="text-gray-900">{campaign.estimated_recipients}</dd>
                            </div>
                            <div className="flex justify-between">
                              <dt className="text-gray-500">Segments:</dt>
                              <dd className="text-gray-900">{campaign.target_segment_count || 1}</dd>
                            </div>
                          </dl>
                        </div>

                        {campaign.stats && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-900 mb-2">Performance</h5>
                            <dl className="space-y-1 text-sm">
                              <div className="flex justify-between">
                                <dt className="text-gray-500">Total Sent:</dt>
                                <dd className="text-gray-900">{campaign.stats.sent}</dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-500">Opens:</dt>
                                <dd className="text-gray-900">
                                  {campaign.stats.opens} ({formatRate(campaign.stats.open_rate)})
                                </dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-500">Clicks:</dt>
                                <dd className="text-gray-900">
                                  {campaign.stats.clicks} ({formatRate(campaign.stats.click_rate)})
                                </dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-500">Bounced:</dt>
                                <dd className="text-gray-900">{campaign.stats.bounced}</dd>
                              </div>
                              <div className="flex justify-between">
                                <dt className="text-gray-500">Unsubscribed:</dt>
                                <dd className="text-gray-900">{campaign.stats.unsubscribed}</dd>
                              </div>
                            </dl>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignHistory;